import {matchPath} from 'react-router-dom'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {DEFAULT_EDITORIAL_PATH_PREFIX} from '../data/constants'

/**
 * How the storefront should follow a redirect coming from a backend response
 * (search `c_searchRedirect*`, url-mapping `c_destinationUrl`, category `c_canonicalUrl`, ...).
 *
 * - `pwa-routing`: destination is served by the PWA → use React Router (no full reload).
 * - `hard-navigation`: destination is served outside the PWA (SFRA) → full page load.
 */
export type RedirectMode = 'pwa-routing' | 'hard-navigation'

/** Classification of a destination path (locale already stripped). */
export type DestinationKind = 'editorial-pwa' | 'editorial-sfra' | 'plp-pwa' | 'pdp-pwa' | 'unknown'

export interface RedirectResult {
    /** Whether a redirect must be followed. */
    shouldRedirect: boolean
    /**
     * Locale-relative path (e.g. `/en-it/stories/foo.html`, `/en-it/donna/how-to-style`),
     * preserving query string and hash. Used for `pwa-routing` (React Router).
     */
    target?: string
    /**
     * Original absolute URL from the backend (e.g. `https://host/en-it/vicuna.html`).
     * Used for `hard-navigation` so the browser reaches the correct public domain. `undefined`
     * when the backend URL was relative.
     */
    absoluteUrl?: string
    /** How to follow the redirect. Meaningful only when `shouldRedirect` is `true`. */
    mode?: RedirectMode
    /** Debug/analytics detail on how the destination was classified. */
    kind?: DestinationKind
}

/** Backwards-compatible alias for the search flow. */
export type SearchRedirectMode = RedirectMode
export type SearchRedirectResult = RedirectResult

interface SearchRedirectSource {
    searchPhraseSuggestions?: {
        [x: `c_${string}`]: string | number
    } | null
}

/**
 * Sentinel base origin used only to let `new URL(url, base)` parse *relative* backend URLs
 * (e.g. `/en-it/vicuna.html`), which the `URL` constructor rejects without a base. It is NOT
 * a development/localhost reference and is never used for navigation: after parsing, an origin
 * still equal to this sentinel means the backend URL was relative, so `absoluteUrl` stays
 * `undefined`. The concrete value is irrelevant as long as it is a valid, fixed origin.
 */
const RELATIVE_URL_SENTINEL_ORIGIN = 'https://localhost'

/**
 * Resolves the configured editorial path prefix (e.g. `stories`), falling back to the
 * static constant when `getConfig()` is not available (e.g. outside the app runtime).
 */
function getEditorialPathPrefix(): string {
    try {
        return getConfig()?.customParams?.editorialPathPrefix || DEFAULT_EDITORIAL_PATH_PREFIX
    } catch {
        return DEFAULT_EDITORIAL_PATH_PREFIX
    }
}

/**
 * Strips the leading locale segment (`/en-it`, `/it-it`, ...) from a path.
 * Returns the remaining path always starting with `/`.
 */
function stripLocaleSegment(pathname: string): string {
    const withoutLocale = pathname.replace(/^\/[a-z]{2}-[a-z]{2}(?=\/|$)/i, '')
    return withoutLocale === '' ? '/' : withoutLocale
}

/**
 * @name classifyDestination
 *
 * @description
 * Classifies a locale-relative destination path into the kind of page that serves it.
 *
 * The rules are derived from the storefront URL structure (see routes.tsx) and are shared by
 * every redirect flow (search, breadcrumb/PLP, future PDP/editorial modules):
 *
 * - `/{prefix}/**` (e.g. `/stories/foo.html`)        → editorial served by PWA
 * - `*.html` with a single leading segment            → editorial served by SFRA (e.g. `/vicuna.html`)
 * - `/{col}/{cat}[/{sub}]/{slug}.html`                → PDP served by PWA
 * - path without `.html` (e.g. `/donna/how-to-style`) → PLP served by PWA
 * - anything else                                     → unknown (treated as SFRA)
 *
 * @param pathWithoutLocale - Destination path with the locale segment already removed.
 * @returns The {@link DestinationKind}.
 */
export function classifyDestination(pathWithoutLocale: string): DestinationKind {
    const prefix = getEditorialPathPrefix()

    // Editorial migrated to PWA lives under the configured prefix (folder `stories`).
    if (pathWithoutLocale === `/${prefix}` || pathWithoutLocale.startsWith(`/${prefix}/`)) {
        return 'editorial-pwa'
    }

    const isHtml = /\.html$/i.test(pathWithoutLocale)
    // Count path segments (ignore leading/trailing slashes).
    const segments = pathWithoutLocale.split('/').filter(Boolean)

    if (isHtml) {
        // PDP URLs carry the category context: /{collection}/{category}[/{subcategory}]/{slug}.html
        // (3 or 4 segments). A single-segment .html (e.g. /vicuna.html) is an SFRA editorial page.
        const isPdp = Boolean(
            matchPath(pathWithoutLocale, {
                path: '/:collectionId/:categoryId/:subcategoryId?/:productSlug.html',
                exact: true,
            }),
        )
        if (isPdp && segments.length >= 3) {
            return 'pdp-pwa'
        }
        return 'editorial-sfra'
    }

    // No .html → PLP (category listing) handled by the PWA catch-all routes.
    // /:collectionId  or  /:collectionId/:categoryId/:subcategoryId
    const isPlp = Boolean(
        matchPath(pathWithoutLocale, {path: '/:collectionId', exact: true}) ||
            matchPath(pathWithoutLocale, {path: '/:collectionId/:categoryId/:subcategoryId', exact: true}) ||
            matchPath(pathWithoutLocale, {path: '/:collectionId/:categoryId', exact: true}),
    )
    return isPlp ? 'plp-pwa' : 'unknown'
}

/**
 * @name resolveRedirect
 *
 * @description
 * Pure, isomorphic helper that turns a backend redirect (status + absolute URL) into an
 * actionable decision for the client: whether to redirect, to which target, and how
 * (React Router vs hard navigation).
 *
 * The backend always returns absolute URLs and does not know whether a destination is PWA or
 * SFRA — that classification is done here, entirely on the PWA side, from the URL structure.
 *
 * @param input.url - Absolute (or relative) destination URL from the backend.
 * @param input.status - HTTP status of the redirect; only `301` triggers a redirect.
 * @returns A {@link RedirectResult}.
 */
export function resolveRedirect(input: {url: unknown; status: unknown}): RedirectResult {
    const {url, status} = input

    // getUrlMapping returns statusCode as a string ("301" / "302"); productSearch uses a number
    // (301). Both permanent (301) and temporary (302) redirects must be followed.
    const normalizedStatus = typeof status === 'string' ? parseInt(status, 10) : status
    const isRedirectStatus = normalizedStatus === 301 || normalizedStatus === 302

    if (!isRedirectStatus || typeof url !== 'string' || url.length === 0) {
        return {shouldRedirect: false}
    }

    let parsed: URL
    try {
        parsed = new URL(url, RELATIVE_URL_SENTINEL_ORIGIN)
    } catch {
        return {shouldRedirect: false}
    }

    const target = `${parsed.pathname}${parsed.search}${parsed.hash}`
    const pathWithoutLocale = stripLocaleSegment(parsed.pathname)
    const kind = classifyDestination(pathWithoutLocale)

    const isPwa = kind === 'editorial-pwa' || kind === 'plp-pwa' || kind === 'pdp-pwa'

    // Only expose an absolute URL when the backend actually provided one. If `url` was relative,
    // `parsed` carries the sentinel origin above, which must not be used for navigation.
    // The second condition covers the edge case where the backend genuinely returns an absolute
    // URL pointing at `localhost` (which would otherwise be indistinguishable from the sentinel).
    const isAbsoluteUrl = parsed.origin !== RELATIVE_URL_SENTINEL_ORIGIN || /^https?:\/\/localhost/i.test(url)

    return {
        shouldRedirect: true,
        target,
        absoluteUrl: isAbsoluteUrl ? parsed.href : undefined,
        mode: isPwa ? 'pwa-routing' : 'hard-navigation',
        kind,
    }
}

/**
 * @name resolveSearchRedirect
 *
 * @description
 * Thin wrapper of {@link resolveRedirect} for the product-search flow, reading the redirect
 * fields from `searchPhraseSuggestions.c_searchRedirect*`.
 *
 * Search redirects only ever target editorial pages, but the shared classification also
 * handles PLP/PDP so the same result shape is reused across flows.
 *
 * @param response - The product search response (only `searchPhraseSuggestions` is read).
 * @returns A {@link RedirectResult}.
 */
export function resolveSearchRedirect(response: SearchRedirectSource | null | undefined): RedirectResult {
    const suggestions = response?.searchPhraseSuggestions
    return resolveRedirect({
        url: suggestions?.c_searchRedirectUrl,
        status: suggestions?.c_searchRedirectStatus,
    })
}

/**
 * @name resolveSuggestionsRedirect
 *
 * @description
 * Thin wrapper of {@link resolveRedirect} for the header autocomplete flow (`getSearchSuggestions`
 * with `c_headerSuggestions=true`).
 *
 * ⚠️ The redirect fields live in a **different** container than `productSearch`: the
 * `getSearchSuggestions` root (`SuggestionResult`) does not support custom `c_*` properties, so
 * the backend exposes them under `contentSuggestions` (which does), i.e.
 * `contentSuggestions.c_searchRedirectUrl` / `contentSuggestions.c_searchRedirectStatus`.
 * This lets the header anticipate the redirect at submit time (no intermediate search page).
 *
 * Accepts `unknown` so the SDK's `SuggestionResult` type can be passed directly; fields are read
 * defensively.
 *
 * @param suggestions - The `getSearchSuggestions` response.
 * @returns A {@link RedirectResult}.
 */
export function resolveSuggestionsRedirect(suggestions: unknown): RedirectResult {
    const content = (suggestions as {contentSuggestions?: Record<string, unknown> | null} | null | undefined)?.contentSuggestions
    return resolveRedirect({
        url: content?.c_searchRedirectUrl,
        status: content?.c_searchRedirectStatus,
    })
}
